import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import classNames from 'classnames';
import { Dropdown } from '../../popups/Dropdown';
import { hasValue } from '../../../../utils';
import { UXPContext } from '@core/context/Context';
import { getInputSizeStyles, getInputStateClass } from './utils';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import ButtonComponent from '@widget/buttons/ButtonComponent';
/**
 * A free-text input that shows a suggestion dropdown as the user types.
 * Unlike `Select`, the user can type any value — suggestions are optional helpers.
 *
 * @example
 * ```tsx
 * // Static options — component filters automatically
 * <AutoCompleteInput
 *   value={val}
 *   onChange={setVal}
 *   options={['India', 'Japan', 'China', 'Singapore']}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Custom dropdown content
 * const inputRef = React.useRef(null)
 *
 * function renderAutoFill() {
 *   return (
 *     <div>
 *       {results.map((r, i) => (
 *         <div key={i} onClick={() => { onChange(r.name); inputRef.current?.close(); }}>
 *           {r.name}
 *         </div>
 *       ))}
 *     </div>
 *   )
 * }
 *
 * <AutoCompleteInput value={val} onChange={setVal} autoFill={renderAutoFill} ref={inputRef} />
 * ```
 *
 * @export
 *
 * @category Inputs/Selection
 */
const AutoCompleteInput = React.forwardRef((props, ref) => {
    const context = React.useContext(UXPContext);
    const { value, onChange, options, autoFill, className, isValid, placeholder: _placeholder, tabIndex, disabled, readOnly, fullWidth, minWidth, maxWidth, width, onClear, addNewValues, } = props;
    const [showPicker, setShowPicker] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState(-1);
    const [savingNewValue, setSavingNewValue] = React.useState(false);
    const containerRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const suggestionsRef = React.useRef(null);
    const autoFillContainerRef = React.useRef(null);
    const placeholder = hasValue(_placeholder) ? _placeholder : context.$L('uxp-core.text.type-or-select-to-add');
    const stateClass = getInputStateClass({ isValid, disabled, readOnly });
    const sizeStyles = getInputSizeStyles({ fullWidth, minWidth, maxWidth, width });
    // Filtered options based on current value
    const suggestions = React.useMemo(() => {
        if (!options)
            return [];
        if (!(value === null || value === void 0 ? void 0 : value.trim()))
            return options;
        return options.filter(o => o.toLowerCase().includes(value.toLowerCase()));
    }, [options, value]);
    // Scroll active suggestion into view (options mode)
    React.useEffect(() => {
        if (activeIndex < 0 || !suggestionsRef.current)
            return;
        const item = suggestionsRef.current.children[activeIndex];
        if (item)
            item.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);
    // Apply --active class and scroll for autoFill mode
    React.useEffect(() => {
        if (!autoFill || !autoFillContainerRef.current)
            return;
        const items = Array.from(autoFillContainerRef.current.querySelectorAll('.uxpcore_autocomplete__suggestion-item'));
        items.forEach((item, i) => {
            item.classList.toggle('uxpcore_autocomplete__suggestion-item--active', i === activeIndex);
        });
        if (activeIndex >= 0 && items[activeIndex]) {
            items[activeIndex].scrollIntoView({ block: 'nearest' });
        }
    }, [activeIndex, autoFill, showPicker]);
    React.useImperativeHandle(ref, () => ({
        open: () => setShowPicker(true),
        close: () => setShowPicker(false),
        focus: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
        getInputElement: () => inputRef.current,
        appendAtCursor: (text) => {
            var _a, _b;
            const el = inputRef.current;
            if (!el)
                return;
            const start = (_a = el.selectionStart) !== null && _a !== void 0 ? _a : value.length;
            const end = (_b = el.selectionEnd) !== null && _b !== void 0 ? _b : value.length;
            onChange(value.substring(0, start) + text + value.substring(end));
            // Restore focus and move cursor after inserted text
            requestAnimationFrame(() => {
                el.focus();
                el.setSelectionRange(start + text.length, start + text.length);
            });
        }
    }));
    function handleChange(e) {
        onChange(e.target.value);
        setShowPicker(true);
        setActiveIndex(-1);
    }
    function handleFocus() {
        setShowPicker(true);
    }
    // Match Select's lifecycle: open on focus / click; close ONLY on click
    // outside (handled by <Dropdown onToggle>), Escape, or selection.
    // Intentionally no blur-close — blur fires on unrelated focus moves
    // (tabbing to a button, opening a nested dialog) and the resulting
    // race with <Dropdown>'s outside-click detection produces flickering
    // and stuck-closed states.
    function handleKeyDown(e) {
        if (e.key === 'Escape') {
            setShowPicker(false);
            setActiveIndex(-1);
            return;
        }
        if (autoFill) {
            // Convention-based nav for custom autoFill renders:
            // consumer adds class 'uxpcore_autocomplete__suggestion-item' to each option element
            const container = autoFillContainerRef.current;
            if (!container)
                return;
            const items = Array.from(container.querySelectorAll('.uxpcore_autocomplete__suggestion-item'));
            if (!items.length)
                return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIndex(prev => Math.min(prev + 1, items.length - 1));
            }
            else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex(prev => Math.max(prev - 1, 0));
            }
            else if (e.key === 'Enter' && activeIndex >= 0 && items[activeIndex]) {
                e.preventDefault();
                items[activeIndex].click();
            }
            return;
        }
        // Built-in keyboard nav for the static options mode
        if (!options)
            return;
        /* The "add new" footer button (when present) occupies one slot
         * past the last suggestion in the keyboard-nav cycle. */
        const addNewActive = !!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable) &&
            (!!addNewValues.alwaysShow || suggestions.length === 0);
        const maxIndex = suggestions.length - 1 + (addNewActive ? 1 : 0);
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => Math.min(prev + 1, maxIndex));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => Math.max(prev - 1, 0));
        }
        else if (e.key === 'Enter') {
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
                e.preventDefault();
                onChange(suggestions[activeIndex]);
                setShowPicker(false);
                setActiveIndex(-1);
            }
            else if (addNewActive && activeIndex === suggestions.length) {
                e.preventDefault();
                triggerAddNew();
            }
        }
    }
    function triggerAddNew() {
        var _a;
        if (!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable))
            return;
        /* Scenario B: host opens a form / dialog / etc. */
        if (addNewValues.onClick) {
            addNewValues.onClick();
            setShowPicker(false);
            return;
        }
        /* Scenario A: async create-from-typed-text. */
        if (!hasValue(value))
            return;
        setSavingNewValue(true);
        Promise.resolve((_a = addNewValues.onAddNewValue) === null || _a === void 0 ? void 0 : _a.call(addNewValues, value))
            .then((res) => {
            const next = typeof res === 'string' && hasValue(res) ? res : value;
            onChange(next);
            setShowPicker(false);
            setActiveIndex(-1);
        })
            .catch(() => { })
            .finally(() => setSavingNewValue(false));
    }
    const addNewElement = (addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable)
        ? (_jsx("div", { className: "uxpcore_dropdown-action-row", onMouseDown: (e) => {
                /* Block input blur so the dropdown's <Dropdown> doesn't
                 * tear down before the click resolves. The whole row is
                 * the click target; the inner button is purely visual
                 * (pointer-events disabled in SCSS) to avoid double-fire. */
                e.preventDefault();
                if (!savingNewValue)
                    triggerAddNew();
            }, children: _jsx(ButtonComponent, { title: savingNewValue && addNewValues.loadingTitle ? addNewValues.loadingTitle : addNewValues.title, onClick: () => { }, disabled: savingNewValue, size: "smaller", variant: "link" }) }))
        : null;
    const addNewAlwaysShow = !!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable) && !!addNewValues.alwaysShow;
    // Build dropdown content and open state
    let dropdownContent = null;
    let isDropdownOpen = false;
    /* In every mode the "+ Add new" affordance is rendered as a sibling
     * outside the scrollable suggestion area so it stays pinned at the
     * bottom of the dropdown regardless of how long the list is. */
    if (autoFill) {
        dropdownContent = (_jsxs("div", { className: "uxpcore_autocomplete__dropdown", children: [_jsx("div", { ref: autoFillContainerRef, children: autoFill() }), addNewAlwaysShow && addNewElement] }));
        isDropdownOpen = showPicker;
    }
    else if (options) {
        /* Open the dropdown even when there are no matches if the
         * "add new" button is configured to be visible (Scenario A's
         * empty-match case OR Scenario B's always-show case). */
        const hasAddNewToShow = !!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable) && (addNewAlwaysShow || suggestions.length === 0);
        isDropdownOpen = showPicker && (suggestions.length > 0 || hasAddNewToShow);
        dropdownContent = (_jsxs("div", { className: "uxpcore_autocomplete__dropdown", children: [_jsx("div", { className: "uxpcore_autocomplete__suggestions", ref: suggestionsRef, children: suggestions.map((o, i) => (_jsx("div", { className: classNames('uxpcore_autocomplete__suggestion-item', {
                            'uxpcore_autocomplete__suggestion-item--active': i === activeIndex,
                            'uxpcore_autocomplete__suggestion-item--selected': o === value,
                        }), onMouseDown: (e) => {
                            e.preventDefault(); // prevent blur before click
                            onChange(o);
                            setShowPicker(false);
                            setActiveIndex(-1);
                        }, onMouseEnter: () => setActiveIndex(i), children: o }, i))) }), hasAddNewToShow && addNewElement] }));
    }
    else if (addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable) {
        /* No options and no autoFill — but consumer wants an add-new
         * affordance. Show a bare button so Scenario B works in isolation. */
        isDropdownOpen = showPicker;
        dropdownContent = (_jsx("div", { className: "uxpcore_autocomplete__dropdown", children: addNewElement }));
    }
    const clearButton = ((value === null || value === void 0 ? void 0 : value.trim().length) > 0 && !disabled && !readOnly) ? (_jsx("div", { className: "uxpcore_input__clear-btn", onMouseDown: (e) => { e.preventDefault(); onClear ? onClear() : onChange(''); setShowPicker(false); }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times'] }) })) : null;
    return (_jsxs("div", { ref: containerRef, className: classNames('uxpcore_autocomplete', 'uxpcore_input', className, stateClass, {
            'uxpcore_input--focus': showPicker,
            'uxpcore_input--has-content': (value === null || value === void 0 ? void 0 : value.trim().length) > 0,
            'uxpcore_input--has-suffix': !!clearButton,
            'uxpcore_input--full-width': fullWidth,
        }), style: sizeStyles, children: [_jsx("input", { ref: inputRef, className: "uxpcore_input__input", type: "text", value: value, onChange: handleChange, onKeyDown: handleKeyDown, onFocus: handleFocus, placeholder: placeholder, tabIndex: tabIndex !== null && tabIndex !== void 0 ? tabIndex : 0, disabled: disabled, readOnly: readOnly, dir: context.direction || 'ltr' }), clearButton && (_jsx("div", { className: "uxpcore_input__suffix", children: clearButton })), dropdownContent && (_jsx(Dropdown, { externalTriggerRef: containerRef, content: dropdownContent, isOpen: isDropdownOpen, onToggle: () => setShowPicker(false), position: "bottom-left", matchTriggerWidth: true }))] }));
});
export default AutoCompleteInput;
